import { useAppSelector } from "app/hooks";
import { TournamentDriverStats } from "components/TournamentDriverStats/TournamentDriverStats";
import { Panel } from "components/Panel/Panel";
import { TrackList } from "components/TrackList/TrackList";
import { Filter } from "model/Filter/Filter";
import { GrowColumn } from "pages/styles";
import { useState } from "react";
import { shallowEqual } from "react-redux";
import {
  selectTournamentSegmentNames,
  selectTournamentTracks,
  selectTournamentSegmentsFromFilters,
  selectTournamentHasTracks,
} from "selectors/Tournament";
import { getSegmentMatchIds } from "model/Tournament/Segment/TournamentSegment";
import { Theme } from "model/Theme";
import { MultiToggleFilter } from "components/Filters/MultiToggleFilter/MultiToggleFilter";
import { FilterContainer } from "components/Filters/styles";
import { FilterRow } from "components/FilterPanels/styles";
import { useMultiSegmentFilter } from "model/Filter/TournamentFilters";
import { EmptyGrowPanelText, NoteText } from "styles/text";
import { TournamentTrack } from "model/Tournament/TournamentTrack";
import { TabBar } from "components/TabBar/TabBar";
import { TrackDriverStats } from "components/Track/Stats/TrackDriverStats";
import { RatingType } from "stats/Rating/RatingTypes";

interface Props {
  tournamentId: string;
  theme?: Theme;
}

export const StatsTab = ({ tournamentId, theme }: Props) => {
  const tournamentHasTracks = useAppSelector((state) =>
    selectTournamentHasTracks(state, tournamentId)
  );

  return (
    <GrowColumn>
      {tournamentHasTracks ? (
        <>
          <Panel type="information">
            <EmptyGrowPanelText>
              We're aware that large tournaments may have slower loading times.
            </EmptyGrowPanelText>
          </Panel>
          <Panel>
            <TrackList tournamentId={tournamentId} />
          </Panel>
          <TournamentStats tournamentId={tournamentId} theme={theme} />
        </>
      ) : (
        <Panel>
          <EmptyGrowPanelText>
            No tracks are currently linked to the tournament
          </EmptyGrowPanelText>
        </Panel>
      )}
    </GrowColumn>
  );
};

// todo - consider seperating out this into a seperate file
const TournamentStats = ({ tournamentId, theme }: Props) => {
  const segmentFilter = useMultiSegmentFilter(tournamentId);
  const selectedSegments =
    useAppSelector(
      (state) =>
        selectTournamentSegmentsFromFilters(
          state,
          tournamentId,
          segmentFilter.selectedValues
        ),
      shallowEqual
    ) ?? [];

  const allSegments = useAppSelector(
    (state) => selectTournamentSegmentNames(state, tournamentId) ?? [],
    shallowEqual
  );

  const tracks = useAppSelector(
    (state) => selectTournamentTracks(state, tournamentId) ?? [],
    shallowEqual
  );

  const tabOptions = ["Overview", ...tracks.map((t) => t.name)];
  const [selectedTab, setSelectedTab] = useState(tabOptions[0]);
  const tabFilter: Filter<string> = {
    options: tabOptions,
    selectedValue: selectedTab,
    setSelectedValue: setSelectedTab,
  };

  const ratingType = getRatingType(
    selectedTab,
    allSegments.length,
    selectedSegments.length
  );

  const matchIds = selectedSegments.flatMap(getSegmentMatchIds);

  return (
    <>
      <Panel padding="padded">
        <FilterContainer>
          <FilterRow>
            <MultiToggleFilter filter={segmentFilter} header="Segments" />
          </FilterRow>
        </FilterContainer>
        <NoteText style={{ paddingTop: "10px" }}>
          Stats may be slightly inaccurate due to incomplete match data
        </NoteText>
      </Panel>
      {selectedSegments.length > 0 && (
        <Panel>
          <TabBar
            filter={tabFilter}
            primaryColor={theme?.primary}
            secondaryColor={theme?.secondary}
          />
          {getTabContent(
            matchIds,
            tabFilter.selectedValue,
            tournamentId,
            theme,
            tracks,
            ratingType
          )}
        </Panel>
      )}
    </>
  );
};

const getTabContent = (
  matchIds: string[],
  selectedTab: string,
  tournamentId: string,
  theme: Theme | undefined,
  tracks: TournamentTrack[],
  ratingType: RatingType
) => {
  switch (selectedTab) {
    case "Overview":
      return (
        <TournamentDriverStats // todo - potentially new system for tournament driver stats
          tournamentId={tournamentId}
          matchIds={matchIds}
          priorMatchIds={[]}
          primaryColor={theme?.primary}
          secondaryColor={theme?.secondary}
          ratingType={ratingType}
          trackIds={tracks.map((t) => t.id)}
        />
      );
    default:
      const trackId = tracks.find((t) => t.name === selectedTab)?.id;
      if (!trackId) {
        return <EmptyGrowPanelText>Unable to find track</EmptyGrowPanelText>;
      }

      return (
        <TrackDriverStats
          matchIds={matchIds}
          trackId={trackId}
          ratingType={ratingType}
        />
      );
  }
};

// Determine type based on number of selected segments relative to all segments
const getRatingType = (
  selectedTab: string,
  numSegments: number,
  numSelectedSegments: number
): RatingType => {
  if (selectedTab === "Overview") {
    if (numSelectedSegments === 1) {
      return "Segment";
    } else if (numSelectedSegments < numSegments) {
      return "Segments";
    }
  }

  if (numSelectedSegments === 1) {
    return "Segment Track";
  } else if (numSelectedSegments < numSegments) {
    return "Segments Track";
  }

  return "Tournament Track";
};
